import axios from 'axios';
import { message } from 'antd';

// 创建axios实例
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 15000, // 减少默认超时时间到15秒
  headers: {
    'Content-Type': 'application/json',
  },
});

// 调试：打印baseURL配置
console.log('🔧 API配置 - baseURL:', api.defaults.baseURL);

// 重试配置
let retryCount = 0;
const maxRetries = 5; // 增加重试次数
const retryDelay = 1000;
const maxRetryDelay = 10000; // 最大重试延迟

// 全局导航函数
let navigateFunction: ((path: string) => void) | null = null;

export const setNavigateFunction = (navigate: (path: string) => void) => {
  navigateFunction = navigate;
};

// 调试功能 - 使用全局事件系统
const debugLog = (type: string, data: any) => {
  const debugMode = localStorage.getItem('debugMode') === 'true';
  if (debugMode) {
    console.log(`[API DEBUG ${type}]`, data);
    
    // 发送事件到调试上下文
    const debugEvent = new CustomEvent('debug-info', {
      detail: {
        key: `api.${type}.${Date.now()}`,
        data: {
          data,
          timestamp: new Date().toISOString()
        }
      }
    });
    window.dispatchEvent(debugEvent);
    
    // 同时存储到localStorage作为备份
    const debugInfo = JSON.parse(localStorage.getItem('globalDebugInfo') || '{}');
    debugInfo[`api.${type}.${Date.now()}`] = {
      data,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('globalDebugInfo', JSON.stringify(debugInfo));
  }
};

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 找回密码相关的请求不需要添加认证 token
    const isForgotPasswordRequest = config.url?.includes('/forgot-password');
    
    // 从localStorage获取token（找回密码请求除外）
    if (!isForgotPasswordRequest) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // 调试日志
    debugLog('request', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      data: config.data,
      params: config.params
    });
    
    return config;
  },
  (error) => {
    debugLog('request-error', error);
    return Promise.reject(error);
  }
);

// 重试函数
const retryRequest = (error: any) => {
  const config = error.config;
  
  // 检查是否应该重试
  if (!config || retryCount >= maxRetries) {
    retryCount = 0;
    return Promise.reject(error);
  }
  
  // 对于RCON相关请求，不进行重试，避免重复请求
  if (config.url && config.url.includes('/ark-rcon/')) {
    console.log('RCON请求不进行重试:', config.url);
    retryCount = 0;
    return Promise.reject(error);
  }
  
  // 检查错误类型是否适合重试
  const shouldRetry = 
    !error.response && // 没有响应（网络错误）
    (error.code === 'ECONNRESET' || 
     error.code === 'ECONNABORTED' || 
     error.code === 'ENOTFOUND' ||
     error.code === 'ECONNREFUSED' ||
     error.message.includes('timeout') ||
     error.message.includes('Network Error') ||
     error.message.includes('ERR_NETWORK') ||
     error.message.includes('ERR_CONNECTION_RESET'));
  
  if (!shouldRetry) {
    retryCount = 0;
    return Promise.reject(error);
  }
  
  retryCount++;
  console.log(`重试请求 ${retryCount}/${maxRetries}: ${config.url}`);
  
  // 延迟后重试，使用指数退避
  const delay = Math.min(retryDelay * Math.pow(2, retryCount - 1), maxRetryDelay);
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(api(config));
    }, delay);
  });
};

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 重置重试计数
    retryCount = 0;
    
    // 调试日志
    debugLog('response', {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      data: response.data,
      headers: response.headers
    });
    
    return response;
  },
  async (error) => {
    console.error('API Error:', error);
    
    // 调试日志
    debugLog('response-error', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      stack: error.stack,
      code: error.code
    });
    
    // 尝试重试网络错误（但排除RCON请求）
    const shouldRetryError = 
      !error.response && // 没有响应（网络错误）
      !error.config?.url?.includes('/ark-rcon/') && // 排除RCON请求
      (error.code === 'ECONNRESET' || 
       error.code === 'ECONNABORTED' || 
       error.code === 'ENOTFOUND' ||
       error.code === 'ECONNREFUSED' ||
       error.message.includes('timeout') ||
       error.message.includes('Network Error') ||
       error.message.includes('ERR_NETWORK') ||
       error.message.includes('ERR_CONNECTION_RESET'));
    
    if (shouldRetryError) {
      try {
        return await retryRequest(error);
      } catch (retryError) {
        console.error('重试失败:', retryError);
        error = retryError;
      }
    }
    
    if (error.response) {
      const { status, data } = error.response;
      
      // 检查是否是找回密码相关的请求
      const isForgotPasswordRequest = error.config?.url?.includes('/forgot-password');
      
      switch (status) {
        case 401:
          console.log('🚨 收到401错误，当前路径:', window.location.pathname);
          
          // 找回密码相关的请求不需要认证，直接返回错误，不处理401逻辑
          if (isForgotPasswordRequest) {
            console.log('✅ 这是找回密码请求，不处理401认证逻辑');
            break;
          }
          
          // 未授权，清除token
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          
          // 只在非公开页面时跳转，避免无限循环
          const publicPaths = ['/', '/pricing'];
          const isPublicPath = publicPaths.includes(window.location.pathname) || 
                              window.location.pathname.startsWith('/share/');
          
          console.log('🔍 路径检查 - 当前路径:', window.location.pathname, '是否公开路径:', isPublicPath);
          
          if (window.location.pathname !== '/login' && 
              window.location.pathname !== '/register' &&
              window.location.pathname !== '/forgot-password' &&
              !isPublicPath) {
            console.log('🔄 需要跳转到登录页面');
            if (navigateFunction) {
              navigateFunction('/login');
            } else {
              window.location.href = '/login';
            }
            message.error('登录已过期，请重新登录');
          } else {
            console.log('✅ 当前是公开页面或登录页面，不跳转');
          }
          break;
        case 403:
          // 检查是否是淘宝验证错误
          if (data?.code === 'TAOBAO_VERIFICATION_REQUIRED') {
            // 触发全局验证事件
            window.dispatchEvent(new CustomEvent('taobaoVerificationRequired', {
              detail: { serverId: error.config?.url?.match(/\/servers\/(\d+)/)?.[1] }
            }));
            message.warning('此服务器需要验证淘宝收货码后才能使用完整功能');
          } else {
            // 静默处理其他403错误，不显示弹窗
            console.warn('权限不足:', data?.message || '没有权限访问');
          }
          break;
        case 404:
          // 检查是否是优惠码验证请求，如果是则不显示全局错误提示
          const isCouponValidation = error.config?.url?.includes('/admin/coupons/validate');
          if (!isCouponValidation) {
            message.error('请求的资源不存在');
          }
          break;
        case 500:
          message.error('服务器内部错误');
          break;
        default:
          const errorMessage = data?.message || data?.error || '请求失败';
          message.error(errorMessage);
      }
    } else if (error.request) {
      if (error.code === 'ECONNRESET') {
        message.error('连接被重置，正在尝试重新连接...');
      } else if (error.code === 'ECONNABORTED') {
        message.error('连接超时，正在尝试重新连接...');
      } else if (error.code === 'ENOTFOUND') {
        message.error('服务器地址无法找到，请检查网络设置');
      } else if (error.code === 'ECONNREFUSED') {
        message.error('连接被拒绝，服务器可能未启动');
      } else {
        message.error('网络连接失败 - 无法连接到服务器');
      }
    } else {
      const errorMessage = error.message || '请求失败';
      message.error(errorMessage);
    }
    
    return Promise.reject(error);
  }
);

// 货币格式化工具函数
export const formatCurrency = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '0.00';
  }
  
  const num = typeof amount === 'number' ? amount : parseFloat(amount.toString());
  
  if (isNaN(num)) {
    return '0.00';
  }
  
  return num.toFixed(2);
};

// 全局调试模式控制
(window as any).toggleDebugMode = () => {
  const currentMode = localStorage.getItem('debugMode') === 'true';
  const newMode = !currentMode;
  localStorage.setItem('debugMode', newMode.toString());
  console.log(`🔧 调试模式已${newMode ? '开启' : '关闭'}。刷新页面以应用更改。`);
  return newMode;
};

// 显示调试模式状态
(window as any).debugStatus = () => {
  const debugMode = localStorage.getItem('debugMode') === 'true';
  console.log(`🔧 当前调试模式: ${debugMode ? '开启' : '关闭'}`);
  console.log('💡 使用 toggleDebugMode() 来切换调试模式');
  return debugMode;
};

export default api;



